import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy as TrophyIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { trophiesService } from '../../services/trophies';
import LocationHeader from '../../components/location/LocationHeader';
import { CountryStateFields } from '../../components/location/CountryStateFields';
import { GymInfoFields, type GymInfoValues } from '../../components/gym/GymInfoFields';
import RankingStrategySelect from '../components/RankingStrategySelect';
import StatusCard from '../components/StatusCard';
import LocationDocumentsCard from '../components/LocationDocumentsCard';
import { useCurrentTrophy } from '../hooks/useCurrentTrophy';
import type { LifecycleStatus } from '../../types/competition';
import type { TrophyStatus } from '../../types/trophy';
import type { GalleryImage } from '../../types/location-images';

const EMPTY_INFO: GymInfoValues = {
  websiteUrl: '',
  address: '',
  city: '',
  zipcode: '',
  latitude: '',
  longitude: '',
};

/**
 * The circuit's own identity: name, logo, cover, gallery, short description and
 * the organiser's address.
 *
 * Same shape as the gym's info page, with two differences. The **name** is
 * editable here — a gym is named once at sign-up, but a circuit is routinely
 * "Trofeo Lombardia 2027" and gets renamed every season. And the
 * **country/state** pair is on the form, as it is for a crag: a trophy is
 * created from the panel rather than by us, so nobody has set it beforehand,
 * and its stages inherit it.
 */
export default function TrophyInfoPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { trophy, loading, setTrophy, slug } = useCurrentTrophy();

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [info, setInfo] = useState<GymInfoValues>(EMPTY_INFO);
  /** Which regulation the stages are ranked by; empty is the standard one. */
  const [rankingStrategy, setRankingStrategy] = useState('');
  const [saving, setSaving] = useState(false);
  const [movingStatus, setMovingStatus] = useState(false);

  useEffect(() => {
    if (!trophy) return;
    setName(trophy.name);
    setCountry(trophy.country || '');
    setState(trophy.state || '');
    setRankingStrategy(trophy.ranking_strategy ?? '');
    setInfo({
      websiteUrl: trophy.website_url || '',
      address: trophy.address || '',
      city: trophy.city || '',
      zipcode: trophy.zipcode || '',
      latitude: trophy.latitude != null ? String(trophy.latitude) : '',
      longitude: trophy.longitude != null ? String(trophy.longitude) : '',
    });
  }, [trophy]);

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const updated = await trophiesService.updateTrophy(slug, {
        name: name.trim() || undefined,
        country: country || undefined,
        // An empty state is a real value — "this country has none chosen" — so
        // it is sent rather than dropped, or clearing it would never stick.
        state,
        // Sent as it is, empty included: that is how a circuit goes back to
        // the standard ranking.
        ranking_strategy: rankingStrategy,
        website_url: info.websiteUrl || undefined,
        address: info.address || undefined,
        city: info.city || undefined,
        zipcode: info.zipcode || undefined,
        latitude: info.latitude ? parseFloat(info.latitude) : undefined,
        longitude: info.longitude ? parseFloat(info.longitude) : undefined,
      });
      setTrophy(updated);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status: LifecycleStatus) => {
    if (!slug) return;
    setMovingStatus(true);
    try {
      setTrophy(await trophiesService.updateStatus(slug, status as TrophyStatus));
      toast({ title: t('gymAdmin.lifecycle.moved') });
    } catch (error) {
      // The server refuses a move the life cycle does not allow, and says why
      // — a circuit people have entered cannot go back to draft.
      toast({
        title: t('gymAdmin.lifecycle.moveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setMovingStatus(false);
    }
  };

  if (loading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!trophy) return null;

  return (
    <div className="max-w-4xl space-y-6">
      <StatusCard
        kind="trophy"
        status={trophy.status}
        available={trophy.available_statuses ?? []}
        saving={movingStatus}
        onChange={handleStatusChange}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.imagesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <LocationHeader
            locationId={trophy.id}
            name={trophy.name}
            bio={trophy.bio}
            coverImageUrl={trophy.cover_image_url}
            logoUrl={trophy.logo_url}
            logoSmallUrl={trophy.logo_small_url}
            galleryImages={trophy.gallery_images ?? []}
            fallbackIcon={<TrophyIcon className="w-8 h-8 text-muted-foreground" />}
            isStaffMember
            isStaffAdmin
            hideName
            onBioUpdated={(bio) => setTrophy((tr) => (tr ? { ...tr, bio } : tr))}
            onCoverUpdated={(url) => setTrophy((tr) => (tr ? { ...tr, cover_image_url: url } : tr))}
            onLogoUpdated={(url, smallUrl) =>
              setTrophy((tr) => (tr ? { ...tr, logo_url: url, logo_small_url: smallUrl } : tr))
            }
            onGalleryUpdated={(imgs: GalleryImage[]) =>
              setTrophy((tr) => (tr ? { ...tr, gallery_images: imgs } : tr))
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.detailsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="trophy-name">{t('gymAdmin.trophyInfo.name')}</Label>
            <Input
              id="trophy-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('gymAdmin.trophyInfo.namePlaceholder')}
            />
          </div>

          <CountryStateFields
            country={country}
            state={state}
            onChange={(patch) => {
              if (patch.country !== undefined) setCountry(patch.country);
              if (patch.state !== undefined) setState(patch.state);
            }}
          />

          {/* Every stage's places, unless a stage says otherwise. */}
          <RankingStrategySelect
            id="trophy-ranking-strategy"
            value={rankingStrategy}
            options={trophy.ranking_strategies ?? []}
            onChange={setRankingStrategy}
          />

          <GymInfoFields
            values={info}
            onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))}
          />

          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </CardContent>
      </Card>

      {/* The regulation and the notices: read only from here for now. */}
      {slug && <LocationDocumentsCard key={slug} slug={slug} stage={false} />}
    </div>
  );
}
